/*
Race naming and bonus wording, shared by whatever surface offers the choice.

Every race used to carry a hardcoded list of promises -- "+25% army stats", "+50% food production"
-- that nothing in the backend implemented. The claims here come from `RACE_BONUSES` in the server
config, which is the same object the simulator reads when it applies them, so a card cannot
advertise a bonus the game does not give.
*/

import i18n from "src/i18n"

const RACES = ["chicken", "warrior", "economist", "technologist", "clumsy", "assassin", "infestor", "intelligent"]

// Race display names in the player's language. A getter-backed object so `raceNames[race]` keeps
// reading naturally at the call sites while resolving through i18n at read time (a plain object
// would freeze the language of whichever module imported it first).
export const raceNames = Object.defineProperties(
  {},
  Object.fromEntries(
    RACES.map((race) => [race, { get: () => i18n.t(`races.${race}`, { defaultValue: race }), enumerable: true }])
  )
)

// The raceless default each team falls back to when a player picks nothing.
export const defaultRaceByTeam = { human: "chicken", zombie: "clumsy" }

// In the order the shop shelves them, which is by price (`race_catalogue` sorts on it): the
// ten-day class first, the thirty-day one last. Two screens listing the same three classes in two
// orders is two shelves for a player to reconcile, and the lobby is where they go looking for the
// one they just read about.
export const racesByTeam = {
  human: ["economist", "warrior", "technologist"],
  zombie: ["infestor", "assassin", "intelligent"],
}

// What each bonus key means in words. `PRODUCTION` is the same rule on both sides but the two teams
// farm different things, so it reads per team.
const bonusLabels = {
  STAT: () => i18n.t("races.bonus.stat"),
  CONSUMPTION: () => i18n.t("races.bonus.consumption"),
  PRODUCTION: (team) => i18n.t(team === "human" ? "races.bonus.productionFood" : "races.bonus.productionMeat"),
  POPULATION_GROWTH: () => i18n.t("races.bonus.populationGrowth"),
  MISSILE_DAMAGE: () => i18n.t("races.bonus.missileDamage"),
  RESEARCH_POINTS: () => i18n.t("races.bonus.researchPoints"),
  BUILDING_COST: () => i18n.t("races.bonus.buildingCost"),
  MOVEMENT: () => i18n.t("races.bonus.movement"),
  ACTIONS: () => i18n.t("races.bonus.actions"),
}

// Movement is a flat number of extra cells; everything else is a fraction. The unit it is counted
// in used to be spelled out — "+2 cells army movement" — and it read as a measurement rather than
// as a promise, next to bonuses that are one short phrase each (owner, 2026-09-10). The label
// carries what it is now ("speed"), so the number stands on its own like the percentages do.
const flatBonuses = new Set(["MOVEMENT", "ACTIONS"])

export const describeBonus = (key, value, team) => {
  const label = bonusLabels[key] ? bonusLabels[key](team) : key.toLowerCase().replace(/_/g, " ")
  if (flatBonuses.has(key)) return `${value > 0 ? "+" : ""}${value} ${label}`
  const percent = Math.round(value * 100)
  return `${percent > 0 ? "+" : ""}${percent}% ${label}`
}

export const unitName = (unit) =>
  i18n.t(`city.units.${unit}`, { defaultValue: unit.replace(/_/g, " ").replace(/^./, (c) => c.toUpperCase()) })

// The unit a race unlocks is whatever it has that the raceless default does not.
export const specialUnitFor = (race, team, unitsPerRace = {}) => {
  const baseUnits = new Set(unitsPerRace[defaultRaceByTeam[team]] || [])
  return (unitsPerRace[race] || []).find((unit) => !baseUnits.has(unit))
}
